import { PrivacyLinks } from "./display-ads/PrivacyLinks";
import { usePublicAdsConfig } from "./display-ads/config";
import { useUsage } from "./lib/use-usage";
import { waterCoolerAudio } from "./lib/water-cooler-audio";
import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { RANDO_CHANNEL } from "@serendiptv/contracts";
import { loadFavorites, saveFavorites, type Favorite } from "./lib/favorites";
import type { ClientCapabilities, PlaybackEvent } from "@serendiptv/contracts";
import {
  fetchAccessStatus,
  fetchGuide,
  fetchItemDetails,
  fetchLiveChannel,
  fetchNextSelection,
  fetchStations,
  postFeedback,
  postPlaybackEvent,
  sendPlaybackBeacon
} from "./lib/api";
import { detectCapabilities } from "./lib/capabilities";
import { directionBetween, wrapChannel } from "./lib/channel";
import { faceFromSelection, formatRuntime } from "./lib/metadata";
import { formatSourceDate } from "./lib/source-date";
import { safeCaptionTrackUrl } from "./lib/captions";
import { loadProfile, makeSessionId, persistProfile } from "./lib/storage";
import { selectionFromLive, type FaceMetadata, type GuideEntry, type GuideResponse, type SelectionResponse, type StationSummary, type StoredProfile } from "./types";
import "./styles.css";
import "./skins/skins.css";
import "./skins/game-skins.css";
import { AccessGate } from "./AccessGate";
import type { GameConsoleHandle } from "./gaming/GameConsole";
import { ARCADE_GAMES } from "./gaming/arcade/catalog";
import CartridgeSlot from "./gaming/CartridgeSlot";
import { AppearanceSelector } from "./skins/AppearanceSelector";
import { loadSkin, persistSkin, type SkinId } from "./skins/skins";
import { TelevisionPanel } from "./TelevisionPanel";
import { usePresence } from "./lib/use-presence";
import { isSafePlaybackUrl as validatePlaybackUrl } from "./lib/media-url";
import "./television-panel.css";

import { WEBCAMS, webcamForChannel, type Webcam } from "./webcams/catalog";

import { GUIDE_CHANNEL, tuningLineup, type TunerStation } from "./guide/station";
import type { GuideChannelHandle } from "./guide/GuideChannel";
const GuideChannel = lazy(() => import("./guide/GuideChannel"));

const WebcamPlayer = lazy(() => import("./webcams/WebcamPlayer"));
const GameConsole = lazy(() => import("./gaming/GameConsole"));

class GameLoadBoundary extends Component<{ active: boolean; children: React.ReactNode; onError: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() {
    if (!this.state.failed) return this.props.children;
    return this.props.active ? (
      <div className="game-load-error" role="status">
        <strong>GAME CONNECTION LOST</strong>
        <p>The console could not start. Switch back to TV, or reload to reconnect.</p>
        <button type="button" onClick={() => window.location.reload()}>RELOAD TELEVISION</button>
      </div>
    ) : null;
  }
}

const DEFAULT_CHANNEL = 26;
const TUNE_DEBOUNCE_MS = 165;
const RECENT_ITEM_LIMIT = 24;
const PLAYBACK_START_TIMEOUT_MS = 20_000;

function isPortraitViewport(): boolean {
  // Match the CSS fallback without depending on delayed MediaQueryList updates.
  return window.innerWidth <= 520 || window.innerWidth <= window.innerHeight;
}

const EMPTY_FACE: FaceMetadata = {
  channel: DEFAULT_CHANNEL,
  title: "TUNING IN…",
  subtitle: null,
  genre: "PROGRAM",
  year: "----",
  era: null,
  network: "LOCAL",
  station: null,
  item: null
};

function toSafeNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function isSafePlaybackUrl(value: string): boolean {
  return validatePlaybackUrl(value, window.location.href, import.meta.env.VITE_MEDIA_ALLOWED_HOSTS as string | undefined);
}

function capabilityWithViewport(capabilities: ClientCapabilities): ClientCapabilities {
  if (typeof window === "undefined") return capabilities;
  return {
    ...capabilities,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    touch_capable: navigator.maxTouchPoints > 0 || "ontouchstart" in window
  };
}

interface PlaybackRuntime {
  sequence: number;
  itemId: string;
  channel: number;
  startedAt: number;
  startOffset: number;
  watchSeconds: number;
  lastMediaTime: number | null;
  lastSampleAt: number;
  startEventSent: boolean;
}

function initialStoredState(): { profile: StoredProfile; available: boolean } {
  return loadProfile();
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function StaticOverlay({ active, noSignal }: { active: boolean; noSignal: boolean }) {
  return (
    <div className={`static-overlay ${active ? "static-overlay-active" : ""} ${noSignal ? "static-overlay-no-signal" : ""}`} aria-hidden="true">
      <div className="static-noise" />
      <div className="static-scan" />
      {noSignal ? <span className="no-signal-copy">NO SIGNAL</span> : null}
    </div>
  );
}

function VolumeControl({
  volume,
  muted,
  audioUnlockHint,
  onVolume,
  onMute
}: {
  volume: number;
  muted: boolean;
  audioUnlockHint: boolean;
  onVolume: (volume: number) => void;
  onMute: () => void;
}) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => onVolume(Number(event.currentTarget.value));
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key.toLowerCase() === "m") {
      event.preventDefault();
      onMute();
    }
  };
  const handlePointerDown = (_event: ReactPointerEvent<HTMLInputElement>) => {
    // Clicking or turning the physical knob is the intentional audio-unlock
    // gesture. It also gives a muted first-session knob a useful detent before
    // a drag begins, even when a browser does not emit a range input event for
    // a center click.
    if (muted) onVolume(Math.max(volume, 0.42));
  };
  const angle = Math.round(volume * 260 - 130);

  return (
    <div className={`volume-module ${audioUnlockHint ? "volume-module-hint" : ""}`}>
      <div className="control-label" aria-hidden="true">VOLUME</div>
      <div className="volume-knob" style={{ "--volume-rotation": `${angle}deg` } as CSSProperties}>
        <div className="volume-face" aria-hidden="true"><span className="volume-pointer" /></div>
        <input
          className="volume-input"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={muted ? 0 : volume}
          aria-label={`Volume ${Math.round(volume * 100)} percent${muted ? ", muted" : ""}. Turn to change sound.`}
          aria-valuetext={`${muted ? 0 : Math.round(volume * 100)} percent${muted ? ", muted" : ""}`}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
        />
      </div>
      <span className={`volume-state ${muted ? "volume-state-muted" : ""}`} aria-live="polite">{muted ? "MUTED" : `${Math.round(volume * 100)}%`}</span>
    </div>
  );
}

type FeedbackState = { itemId: string; signal: "MORE_LIKE_THIS" | "LESS_LIKE_THIS"; status: "pending" | "saved" | "error" };

function InfoPanel({
  face,
  detail,
  loading,
  onClose,
  onFeedback,
  feedbackState,
  closeRef,
  skin,
  onSkinChange,
  favorites,
  favoritesPersistent,
  onFavoritePlay
}: {
  face: FaceMetadata;
  detail: SelectionResponse["item"] | null;
  loading: boolean;
  onClose: () => void;
  onFeedback: (signal: "MORE_LIKE_THIS" | "LESS_LIKE_THIS") => void;
  feedbackState: FeedbackState | null;
  closeRef: React.RefObject<HTMLButtonElement | null>;
  skin: SkinId;
  onSkinChange: (skin: SkinId) => void;
  favorites: Favorite[];
  favoritesPersistent: boolean;
  onFavoritePlay: (favorite: Favorite) => void;
}) {
  const item = detail?.id === face.item?.id ? detail : face.item;
  const runtime = formatRuntime(item?.runtime_seconds);
  const displayNetwork = item?.network || item?.station || face.network || "LOCAL";
  const feedbackStatusRef = useRef<HTMLDivElement | null>(null);
  const previousFeedbackRef = useRef(feedbackState);
  useEffect(() => {
    if (feedbackState && feedbackState !== previousFeedbackRef.current) feedbackStatusRef.current?.scrollIntoView({ block: "nearest" });
    previousFeedbackRef.current = feedbackState;
  }, [feedbackState]);

  return (
    <aside className="service-panel" role="dialog" aria-modal="true" aria-labelledby="service-panel-title">
      <div className="service-panel-header">
        <span className="service-panel-eyebrow">SERENDIPTV SERVICE RECORD</span>
        <button ref={closeRef} className="service-close" type="button" onClick={onClose} aria-label="Close program information">CLOSE ×</button>
      </div>
      <h2 id="service-panel-title">{item?.title || face.title}</h2>
      {item?.subtitle ? <p className="service-subtitle">{item.subtitle}</p> : null}
      {loading ? <p className="service-loading">RETRIEVING PROGRAM NOTES…</p> : null}
      {item?.description ? <p className="service-description">{item.description}</p> : null}
      <dl className="service-facts">
        <div><dt>CHANNEL</dt><dd>{face.channel}</dd></div>
        <div><dt>YEAR / ERA</dt><dd>{face.year} / {face.era ?? "—"}</dd></div>
        <div><dt>TYPE</dt><dd>{face.genre}</dd></div>
        <div><dt>NETWORK</dt><dd>{displayNetwork}</dd></div>
        {item?.original_air_date ? <div><dt>AIR DATE</dt><dd>{formatSourceDate(item.original_air_date) ?? item.original_air_date}</dd></div> : null}
        {item?.creator ? <div><dt>CREATOR</dt><dd>{item.creator}</dd></div> : null}
        {item?.language ? <div><dt>LANGUAGE</dt><dd>{item.language}</dd></div> : null}
        {runtime ? <div><dt>RUNTIME</dt><dd>{runtime}</dd></div> : null}
        {item?.source_collection ? <div><dt>COLLECTION</dt><dd>{item.source_collection}</dd></div> : null}
        {item?.source_identifier ? <div><dt>{item.source_provider === "serendiptv_advertising" ? "PRODUCTION ID" : "ARCHIVE ID"}</dt><dd>{item.source_identifier}</dd></div> : null}
      </dl>
      <AppearanceSelector skin={skin} onSkinChange={onSkinChange} />
      <PrivacyLinks />
      <section className="favorite-records" aria-label="Saved favorites">
        <h3>FAVORITES · THIS BROWSER</h3>
        <p>{favoritesPersistent ? "Save discoveries with the Favorite button on Rando, channel 69." : "Storage is unavailable. Favorites last only for this visit."}</p>
        {favorites.length ? favorites.map((favorite) => <button type="button" className="panel-action" key={favorite.id} onClick={() => onFavoritePlay(favorite)}>{favorite.title}</button>) : <p>No favorites saved yet.</p>}
      </section>
      <div className="service-panel-footer">
        <span className="service-source">{item?.source_provider === "serendiptv_advertising"
          ? "AI-GENERATED ADVERTISEMENT / SERENDIPTV"
          : `ARCHIVAL SIGNAL / ${item?.source_provider?.toUpperCase() || "INTERNET ARCHIVE"}`}</span>
        {item ? (
          <div className="service-feedback" aria-label="Optional program feedback">
            <button type="button" disabled={feedbackState?.status === "pending"} onClick={() => onFeedback("LESS_LIKE_THIS")}>LESS LIKE THIS</button>
            <button type="button" disabled={feedbackState?.status === "pending"} onClick={() => onFeedback("MORE_LIKE_THIS")}>MORE LIKE THIS</button>
          </div>
        ) : null}
        {feedbackState ? <div ref={feedbackStatusRef} role="status" aria-live="polite">
          {feedbackState.status === "pending" ? "Saving feedback…" : feedbackState.status === "saved" ? "Feedback saved." : <>Couldn’t save feedback. <button type="button" className="panel-action" onClick={() => onFeedback(feedbackState.signal)}>Retry feedback</button></>}
        </div> : null}
      </div>
    </aside>
  );
}

function guideTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function GuideRows({
  entries,
  serverTime,
  currentChannel,
  onTune
}: {
  entries: GuideEntry[];
  serverTime: Date;
  currentChannel: number;
  onTune: (channel: number) => void;
}) {
  if (!entries.length) return <p className="service-loading">NO PUBLISHED PROGRAMMING IN THIS WINDOW.</p>;
  return (
    <div className="guide-rows">
      {entries.map((entry) => {
        const onNow = new Date(entry.start_at) <= serverTime && new Date(entry.end_at) > serverTime;
        return (
          <button
            key={`${entry.station_id}-${entry.start_at}`}
            type="button"
            className={`guide-row ${onNow ? "guide-row-on-now" : ""} ${entry.channel === currentChannel ? "guide-row-current-station" : ""}`}
            disabled={!onNow}
            onClick={() => onTune(entry.channel)}
            aria-label={`${entry.call_sign}, ${guideTime(entry.start_at)}, ${entry.title}${onNow ? ", tune live" : ""}`}
          >
            <span className="guide-row-channel">{entry.call_sign}</span>
            <span className="guide-row-time">{guideTime(entry.start_at)}</span>
            <span className="guide-row-title">{entry.title}{entry.subtitle ? <small>{entry.subtitle}</small> : null}</span>
            <span className="guide-row-state">{onNow ? "ON NOW · TUNE" : entry.content_type}</span>
          </button>
        );
      })}
    </div>
  );
}

function GuidePanel({
  guide,
  loading,
  error,
  onRetry,
  currentChannel,
  onClose,
  onTune,
  closeRef
}: {
  guide: GuideResponse | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  currentChannel: number;
  onClose: () => void;
  onTune: (channel: number) => void;
  closeRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const anchor = useMemo(() => ({ server: guide ? Date.parse(guide.server_time) : Date.now(), received: performance.now() }), [guide]);
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const serverTime = new Date(anchor.server + performance.now() - anchor.received);
  const entries = guide?.entries ?? [];
  const now = entries.filter((entry) => new Date(entry.start_at) <= serverTime && new Date(entry.end_at) > serverTime);
  const next = (guide?.stations ?? []).flatMap((station) => entries
    .filter((entry) => entry.station_id === station.id && new Date(entry.start_at) > serverTime)
    .slice(0, 1));
  const today = entries.filter((entry) => {
    const time = new Date(entry.start_at);
    return time > serverTime && time.toDateString() === serverTime.toDateString();
  }).slice(0, 12);
  const tomorrowDate = new Date(serverTime);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = entries.filter((entry) => new Date(entry.start_at).toDateString() === tomorrowDate.toDateString()).slice(0, 12);

  return (
    <aside className="service-panel guide-panel" role="dialog" aria-modal="true" aria-labelledby="guide-panel-title">
      <div className="service-panel-header">
        <span className="service-panel-eyebrow">SERENDIPTV BROADCAST GUIDE</span>
        <button ref={closeRef} className="service-close" type="button" onClick={onClose} aria-label="Close broadcast guide">CLOSE ×</button>
      </div>
      <PrivacyLinks /><h2 id="guide-panel-title">WHAT&apos;S ON</h2>
      <p className="guide-clock">NETWORK TIME {guide ? guideTime(serverTime.toISOString()) : "—"} · PUBLISHED SCHEDULE</p>
      {loading ? <p className="service-loading">READING THE BROADCAST LOG…</p> : null}
      {error ? <div role="status"><p>The published guide is unavailable. Try the connection again.</p><button type="button" className="panel-action" onClick={onRetry}>Retry guide</button></div> : null}
      <section className="guide-section"><h3>WEBCAMS · ACROSS THE USA</h3>
        <div className="guide-rows">
          {WEBCAMS.map((camera) => <button key={camera.channel} type="button" className={`guide-row ${camera.channel === currentChannel ? "guide-row-current-station" : ""}`} onClick={() => onTune(camera.channel)} aria-label={`CAM ${camera.channel}, ${camera.name}, ${camera.location}, tune webcam`}>
            <span className="guide-row-channel">CAM {camera.channel}</span>
            <span className="guide-row-time">WEBCAM</span>
            <span className="guide-row-title">{camera.name}<small>{camera.location}</small></span>
            <span className="guide-row-state">TUNE</span>
          </button>)}
        </div>
        <p className="service-description">Live views from public cameras. Availability varies; providers may show recordings outside live hours.</p>
      </section>
      {!loading && !error && guide ? <>
      <section className="guide-section"><h3>ON NOW</h3><GuideRows entries={now} serverTime={serverTime} currentChannel={currentChannel} onTune={onTune} /></section>
      <section className="guide-section"><h3>NEXT</h3><GuideRows entries={next} serverTime={serverTime} currentChannel={currentChannel} onTune={onTune} /></section>
      <section className="guide-section"><h3>LATER TODAY</h3><GuideRows entries={today} serverTime={serverTime} currentChannel={currentChannel} onTune={onTune} /></section>
      <section className="guide-section"><h3>TOMORROW</h3><GuideRows entries={tomorrow} serverTime={serverTime} currentChannel={currentChannel} onTune={onTune} /></section>
      </> : null}
    </aside>
  );
}

function PortraitFallback() {
  return (
    <section className="portrait-fallback" aria-label="Landscape viewing required">
      <div className="portrait-screen-lines" aria-hidden="true" />
      <div className="portrait-brand">SERENDIP<span>TV</span></div>
      <div className="portrait-message">
        <div className="portrait-glyph" aria-hidden="true">↔</div>
        <h1>ROTATE DISPLAY</h1>
        <p>SerendipTV is tuned for a wide screen.</p>
      </div>
      <div className="portrait-channel" aria-hidden="true">CHANNEL — —</div>
    </section>
  );
}

function GameInfoPanel({ title, closeRef, onClose, gameRef, volume, muted, onVolume, skin, onSkinChange }: {
  title: string;
  closeRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  gameRef: React.RefObject<GameConsoleHandle | null>;
  volume: number;
  muted: boolean;
  onVolume: (volume: number) => void;
  skin: SkinId;
  onSkinChange: (skin: SkinId) => void;
}) {
  const [nextPlayers, setNextPlayers] = useState(1);
  const [nextSkill, setNextSkill] = useState(1);
  const [nextMode, setNextMode] = useState("beginner");
  const [nextLives, setNextLives] = useState(3);
  const arcade = ARCADE_GAMES.find(game => game.title === title);
  return (
    <aside className="service-panel game-info-panel" role="dialog" aria-modal="true" aria-labelledby="game-info-title">
      <div className="service-panel-header">
        <span className="service-panel-eyebrow">SERENDIPTV CARTRIDGE BAY</span>
        <button ref={closeRef} className="service-close" type="button" onClick={onClose} aria-label="Close game information">CLOSE ×</button>
      </div>
      <PrivacyLinks />
      <AppearanceSelector skin={skin} onSkinChange={onSkinChange} />
      <h2 id="game-info-title">{title}</h2>
      <div className="service-feedback game-service-actions">
        <button type="button" disabled={!gameRef.current} onClick={() => { gameRef.current?.reset(); onClose(); }}>RESTART GAME</button>
        <button type="button" disabled={!gameRef.current} onClick={() => { gameRef.current?.loadStarter(); onClose(); }}>INCLUDED GAME</button>
        <label className="game-cartridge-picker">INSERT .NES CARTRIDGE
          <input type="file" accept=".nes" disabled={!gameRef.current} aria-label="Load NES cartridge" onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";
            if (file) { void gameRef.current?.loadFile(file).catch(() => undefined); onClose(); }
          }} />
        </label>
      </div>
      <fieldset className="arcade-cartridge-tray">
        <legend>INCLUDED ARCADE CARTRIDGES</legend>
        <p>Settings below apply when you insert a cartridge. Changing cartridges starts a new game; returning to TV preserves the current game in this tab.</p>
        <div className="arcade-settings">
          <label>Human players <select aria-label="Next game players" value={nextPlayers} onChange={event => setNextPlayers(Number(event.target.value))}>
            {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
          </select></label>
          <label>Carnival / Lady Bug skill <select aria-label="Next game skill" value={nextSkill} onChange={event => setNextSkill(Number(event.target.value))}>
            {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
          </select></label>
          <label>M.U.L.E. mode <select aria-label="Next colony mode" value={nextMode} onChange={event => setNextMode(event.target.value)}>
            <option value="beginner">Beginner</option><option value="standard">Standard</option><option value="tournament">Tournament</option>
          </select></label>
          <label>Lady Bug lives <select aria-label="Next Lady Bug lives" value={nextLives} onChange={event => setNextLives(Number(event.target.value))}>
            <option value={3}>3</option><option value={5}>5</option>
          </select></label>
        </div>
        <p>M.U.L.E. supports 1–4 humans, Carnival and Lady Bug 1–2 alternating players, Zoo Keeper one player.</p>
        <div className="service-feedback arcade-cartridges">
          {ARCADE_GAMES.map(game => <button key={game.id} type="button" disabled={!gameRef.current} onClick={() => {
            gameRef.current?.loadGame(game.id, {players: Math.min(nextPlayers, game.id === "mule" ? 4 : game.id === "zoo-keeper" ? 1 : 2), skill: nextSkill, mode: nextMode, lives: nextLives});
            onClose();
          }}>INSERT {game.title}</button>)}
        </div>
      </fieldset>
      {arcade ? <p className="service-description">{arcade.instructions}</p> : null}
      {title === "Signal Chase" ? <p className="service-description">Collect golden signals to raise your score. Dodge the red interference. A boosts your speed; B brakes for a careful approach.</p> : null}
      <p className="service-description">Use the D-pad to move and A / B to play. Your game pauses while this service plate is open.</p>
      <dl className="service-facts">
        <div><dt>MOVE</dt><dd>Arrow keys or W A S D</dd></div>
        <div><dt>A / B</dt><dd>X or Space / Z or C</dd></div>
        <div><dt>START / SELECT</dt><dd>Enter / Shift (pause in arcade games)</dd></div>
        <div><dt>CARTRIDGES</dt><dd>Choose Signal Chase, an arcade cartridge, or a local .nes file.</dd></div>
        <div><dt>TELEVISION</dt><dd>Flip the switch to TV to return to the live broadcast. Your cartridge stays paused.</dd></div>
      </dl>
      <div className="game-audio-controls">
        <label htmlFor="game-volume">GAME SOUND · {muted ? "MUTED" : `${Math.round(volume * 100)}%`}</label>
        <input id="game-volume" type="range" min="0" max="1" step="0.01" value={muted ? 0 : volume} aria-label="Game volume" onChange={(event) => onVolume(Number(event.currentTarget.value))} />
        <button type="button" disabled={!gameRef.current} onClick={() => onVolume(muted ? Math.max(volume, .42) : 0)}>{muted ? "Enable game sound" : "Mute game sound"}</button>
      </div>
      <p className="service-description">Local cartridges stay in this browser tab and are never uploaded. Use homebrew or games you have permission to play. Uncompressed .nes files only; some cartridges are unsupported.</p>
      <p className="service-source">NES EMULATION · JSNES / APACHE-2.0<br />Signal Chase · MIT license<br />Arcade cartridges · supplied authored recreations, with original pixel art and synthesized effects; no ROMs or extracted recordings. Their original-machine fidelity remains approximate.</p>
    </aside>
  );
}

function WebcamInfo({ camera, onClose, closeRef }: { camera: Webcam; onClose: () => void; closeRef: React.RefObject<HTMLButtonElement | null> }) {
  return <aside className="service-panel webcam-info" role="dialog" aria-modal="true" aria-labelledby="webcam-info-title">
    <div className="service-panel-header"><span className="service-panel-eyebrow">SERENDIPTV WEBCAM</span><button ref={closeRef} className="service-close" type="button" onClick={onClose} aria-label="Close program information">CLOSE ×</button></div>
    <PrivacyLinks /><h2 id="webcam-info-title">{camera.name}</h2>
    <p className="service-subtitle">{camera.location} · United States</p>
    <p className="service-description">{camera.availability}</p>
    <p className="service-description">Use the television volume knob or the camera player controls for sound. If a camera goes offline, retry or tune another channel.</p>
    <dl className="service-facts"><div><dt>CHANNEL</dt><dd>{camera.channel}</dd></div><div><dt>PROVIDER</dt><dd>{camera.provider}</dd></div><div><dt>TYPE</dt><dd>PUBLIC WEBCAM</dd></div></dl>
    <a href={camera.sourceUrl} target="_blank" rel="noopener noreferrer">Watch on {camera.provider} ↗</a>
  </aside>;
}

function GuideChannelInfo({ onClose, closeRef }: { onClose: () => void; closeRef: React.RefObject<HTMLButtonElement | null> }) {
  return <aside className="service-panel" role="dialog" aria-modal="true" aria-labelledby="guide-channel-info-title">
    <div className="service-panel-header"><span className="service-panel-eyebrow">SERENDIPTV · CHANNEL 3</span><button ref={closeRef} className="service-close" type="button" onClick={onClose} aria-label="Close program information">CLOSE ×</button></div>
    <PrivacyLinks /><h2 id="guide-channel-info-title">TV GUIDE</h2>
    <p className="service-description">An around-the-clock window into SerendipTV’s published programming. All times are Eastern. Change stations with the channel rocker; open GUIDE for interactive browsing.</p>
    <dl className="service-facts"><div><dt>MUSIC</dt><dd>Channel Three — original SerendipTV instrumental</dd></div><div><dt>SOUND</dt><dd>8-bit smooth jazz · 90 BPM · synthesized without samples</dd></div><div><dt>CREDITS</dt><dd>Composed and synthesized for SerendipTV using Codex. Included score and recording released under CC0.</dd></div></dl>
    <p className="service-description">Use the volume control to enable sound. Listings continue if the music is unavailable.</p>
  </aside>;
}

function ViewerApp() {
  const [skin, setSkin] = useState(loadSkin);
  const [initialStorage] = useState(initialStoredState);
  const [profile, setProfile] = useState(initialStorage.profile);
  const [storageAvailable] = useState(initialStorage.available);
  const [sessionId] = useState(makeSessionId);
  const ads = usePublicAdsConfig();
  const [capabilities, setCapabilities] = useState<ClientCapabilities>(() => detectCapabilities());
  const [favorites, setFavorites] = useState(loadFavorites);
  const [favoritesPersistent, setFavoritesPersistent] = useState(true);
  const randoHistoryRef = useRef<{ itemId: string; offset: number }[]>([]);
  const randoIndexRef = useRef(-1);
  const randoFailuresRef = useRef(0);
  const [randoIndex, setRandoIndex] = useState(-1);
  const [stations, setStations] = useState<TunerStation[]>([]);
  const [channel, setChannel] = useState(initialStorage.profile.lastChannel ?? DEFAULT_CHANNEL);
  const [face, setFace] = useState<FaceMetadata>(() => ({ ...EMPTY_FACE, channel: initialStorage.profile.lastChannel ?? DEFAULT_CHANNEL }));
  const [webcam, setWebcam] = useState<Webcam | null>(null);
  const webcamRef = useRef<Webcam | null>(null);
  const [webcamPlayback, setWebcamPlayback] = useState({ channel: 0, playing: false });
  const onWebcamPlaying = useCallback((channel: number, playing: boolean) => { setWebcamPlayback({ channel, playing }); if (playing) completeUsageTune(channel); }, []);
  const [guideChannel, setGuideChannel] = useState(false);
  const [guideMounted, setGuideMounted] = useState(false);
  const [guideUsable, setGuideUsable] = useState(false);
  const guideChannelRef = useRef(false);
  const guidePlayerRef = useRef<GuideChannelHandle | null>(null);
  const [selection, setSelection] = useState<SelectionResponse | null>(null);
  const [volume, setVolume] = useState(initialStorage.profile.volume);
  const [muted, setMuted] = useState(initialStorage.profile.muted);
  const [, setAudioUnlocked] = useState(initialStorage.profile.audioUnlocked);
  const [audioUnlockHint, setAudioUnlockHint] = useState(false);
  const [isTuning, setIsTuning] = useState(true);
  const [noSignal, setNoSignal] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [detail, setDetail] = useState<SelectionResponse["item"] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [feedbackState, setFeedbackState] = useState<FeedbackState | null>(null);
  const feedbackControllerRef = useRef<AbortController | null>(null);
  const detailControllerRef = useRef<AbortController | null>(null);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guide, setGuide] = useState<GuideResponse | null>(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [guideError, setGuideError] = useState(false);
  const guideControllerRef = useRef<AbortController | null>(null);
  const [gameMode, setGameMode] = useState(false);
  const [gameMounted, setGameMounted] = useState(false);
  const [gameTitle, setGameTitle] = useState("SIGNAL CHASE");
  const [cartridgeState, setCartridgeState] = useState<"loading" | "ready" | "error">("loading");
  const [cartridgeInsertion, setCartridgeInsertion] = useState(0);
  const [controllerMount, setControllerMount] = useState<HTMLDivElement | null>(null);
  const [gameAudioUnlocked, setGameAudioUnlocked] = useState(false);
  const [viewport, setViewport] = useState(() => ({ portrait: isPortraitViewport() }));
  const { portrait } = viewport;
  const portraitRef = useRef(portrait);
  const portraitSuspendedRef = useRef(portrait);
  const portraitResumeFrameRef = useRef(0);
  const gameModeRef = useRef(false);
  const gameRef = useRef<GameConsoleHandle | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const usage = useUsage(sessionId, videoRef, !gameMode && !portrait, guideChannel && !gameMode, Boolean(selection && !isTuning && !noSignal && !webcam && !guideChannel && !gameMode));
  const pendingUsageTune = useRef<{ sequence: number; channel: number; user: boolean } | null>(null);
  function completeUsageTune(target: number) {
    const pending = pendingUsageTune.current;
    if (!pending || pending.channel !== target || pending.sequence !== requestSequenceRef.current) return;
    if (usage.lastCompletedChannel !== target) {
      if (pending.user && usage.lastCompletedChannel !== null) usage.event("channel_change");
      if (target === GUIDE_CHANNEL) usage.event("channel3_visit");
    }
    usage.lastCompletedChannel = target;
    pendingUsageTune.current = null;
  }
  const watchingNow = usePresence(videoRef, sessionId, profile.viewerId, !gameMode && !portrait, !isTuning && !noSignal, channel);
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);
  const titleRef = useRef<HTMLButtonElement | null>(null);
  const gameTitleRef = useRef<HTMLButtonElement | null>(null);
  const panelCloseRef = useRef<HTMLButtonElement | null>(null);
  const infoTriggerRef = useRef<HTMLElement | null>(null);
  const profileRef = useRef(profile);
  const currentItemRef = useRef<string | null>(null);
  const recentItemsRef = useRef<string[]>([]);
  const requestControllerRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);
  const tuneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playbackRuntimeRef = useRef<PlaybackRuntime | null>(null);
  const emitPlaybackEventRef = useRef<(event: PlaybackEvent["event"], keepalive?: boolean) => void>(() => undefined);
  const previousChannelRef = useRef(channel);
  const stationsRef = useRef<TunerStation[]>([]);
  const selectionReceivedAtRef = useRef(0);
  const failedStationsRef = useRef(new Set<number>());
  const playbackStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSkinChange = useCallback((nextSkin: SkinId) => {
    setSkin(nextSkin);
    // Keep the finish in memory even if this browser denies persistent storage.
    persistSkin(nextSkin);
  }, []);

  const cartridgeInserted = useCallback((title: string) => {
    setGameTitle(title);
    setCartridgeInsertion((previous) => previous + 1);
  }, []);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    stationsRef.current = stations;
  }, [stations]);

  useEffect(() => {
    volumeRef.current = volume;
    mutedRef.current = muted;
    const video = videoRef.current;
    if (video) {
      video.volume = volume;
      video.muted = muted;
    }
  }, [muted, volume]);

  useEffect(() => {
    const update = () => setCapabilities(capabilityWithViewport(detectCapabilities()));
    window.addEventListener("resize", update, { passive: true });
    return () => window.removeEventListener("resize", update);
  }, []);

  const syncPortrait = useCallback(function syncPortraitViewport(): boolean {
    const matches = isPortraitViewport();
    if (portraitRef.current !== matches) {
      portraitRef.current = matches;
      // Preserve the transition even if React batches portrait and landscape.
      setViewport({ portrait: matches });
    }
    if (matches) {
      portraitSuspendedRef.current = true;
      // A queued play() can start after resize already paused the element.
      videoRef.current?.pause();
      if (playbackRuntimeRef.current) playbackRuntimeRef.current.lastMediaTime = null;
      if (!portraitResumeFrameRef.current) {
        // A brief rotation can coalesce back to the last observed layout size.
        // Watch geometry only while suspended; native rAF also pauses when hidden.
        const awaitLandscape = () => {
          portraitResumeFrameRef.current = 0;
          if (isPortraitViewport()) portraitResumeFrameRef.current = requestAnimationFrame(awaitLandscape);
          else syncPortraitViewport();
        };
        portraitResumeFrameRef.current = requestAnimationFrame(awaitLandscape);
      }
    } else {
      cancelAnimationFrame(portraitResumeFrameRef.current);
      portraitResumeFrameRef.current = 0;
    }
    return matches;
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(max-aspect-ratio: 1 / 1), (max-width: 520px)");
    let frame = 0;
    const update = () => {
      syncPortrait();
      // Reconcile again after layout if resize arrived before viewport geometry.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncPortrait);
    };
    query.addEventListener("change", update);
    window.addEventListener("resize", update, { passive: true });
    // Root layout follows the viewport even when resize notifications lag or drop.
    const observer = new ResizeObserver(syncPortrait);
    observer.observe(document.documentElement);
    update();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(portraitResumeFrameRef.current);
      portraitResumeFrameRef.current = 0;
      cancelAnimationFrame(frame);
      query.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, [syncPortrait]);

  const makePlaybackEvent = useCallback((event: PlaybackEvent["event"], runtime = playbackRuntimeRef.current): PlaybackEvent | null => {
    if (!runtime) return null;
    const video = videoRef.current;
    const duration = video?.duration;
    const completionRatio = duration && Number.isFinite(duration) && duration > 0
      ? Math.min(1, Math.max(0, (video?.currentTime ?? runtime.startOffset) / duration))
      : undefined;
    return {
      session_id: sessionId,
      viewer_id: profileRef.current.viewerId,
      media_item_id: runtime.itemId,
      channel_number: runtime.channel,
      event,
      start_offset_seconds: runtime.startOffset,
      watch_seconds: Math.max(0, runtime.watchSeconds),
      completion_ratio: completionRatio,
      client_context: {
        ...capabilities,
        storage_available: storageAvailable,
        muted,
        browser_visibility: document.visibilityState
      }
    };
  }, [capabilities, muted, sessionId, storageAvailable]);

  const flushWatchTime = useCallback((includeFinalVisibleSample = false) => {
    if (gameModeRef.current || portraitRef.current) return;
    const runtime = playbackRuntimeRef.current;
    const video = videoRef.current;
    if (!runtime || !video || (document.visibilityState === "hidden" && !includeFinalVisibleSample)) return;
    const now = performance.now();
    if (runtime.lastMediaTime !== null && now > runtime.lastSampleAt) {
      const delta = video.currentTime - runtime.lastMediaTime;
      if (delta > 0 && delta < 8) runtime.watchSeconds += delta;
    }
    runtime.lastMediaTime = video.currentTime;
    runtime.lastSampleAt = now;
  }, []);

  const emitPlaybackEvent = useCallback((event: PlaybackEvent["event"], keepalive = false) => {
    flushWatchTime();
    const payload = makePlaybackEvent(event);
    if (!payload) return;
    if (keepalive && !sendPlaybackBeacon(payload)) {
      void postPlaybackEvent(payload, true);
    } else if (!keepalive) {
      void postPlaybackEvent(payload);
    }
  }, [flushWatchTime, makePlaybackEvent]);

  useEffect(() => {
    emitPlaybackEventRef.current = emitPlaybackEvent;
  }, [emitPlaybackEvent]);

  const performLiveTune = useCallback(async (targetChannel: number, recovering = false, replay?: { itemId: string; offset: number; index?: number }, userInitiated = false) => {
    if (gameModeRef.current) return;
    flushWatchTime();
    videoRef.current?.pause();
    if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
    detailControllerRef.current?.abort();
    feedbackControllerRef.current?.abort();
    feedbackControllerRef.current = null;
    setFeedbackState(null);
    setDetailLoading(false);
    if (!recovering) { failedStationsRef.current.clear(); randoFailuresRef.current = 0; }
    if (playbackRuntimeRef.current?.channel === RANDO_CHANNEL) {
      const current = randoHistoryRef.current[randoIndexRef.current];
      if (current && videoRef.current) current.offset = videoRef.current.currentTime;
    }
    if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
    const sequence = ++requestSequenceRef.current;
    pendingUsageTune.current = { sequence, channel: targetChannel, user: userInitiated };
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setIsTuning(true);
    setNoSignal(false);
    setServiceError(null);

    const isGuide = targetChannel === GUIDE_CHANNEL;
    guideChannelRef.current = isGuide;
    setGuideChannel(isGuide);
    if (isGuide) {
      setGuideMounted(true);
      completeUsageTune(GUIDE_CHANNEL);
      emitPlaybackEventRef.current("channel_change");
      playbackRuntimeRef.current = null;
      currentItemRef.current = null;
      webcamRef.current = null;
      setWebcam(null);
      videoRef.current?.pause();
      setSelection(null);
      setDetail(null);
      setChannel(GUIDE_CHANNEL);
      setFace({ ...EMPTY_FACE, channel: GUIDE_CHANNEL, title: "TV GUIDE", subtitle: "Published programming · Eastern time", genre: "TV GUIDE" });
      setIsTuning(false);
      setProfile((previous) => {
        const next = { ...previous, lastChannel: GUIDE_CHANNEL };
        persistProfile(next);
        return next;
      });
      return;
    }

    const camera = webcamForChannel(targetChannel);
    webcamRef.current = camera ?? null;
    setWebcam(camera ?? null);
    if (camera) {
      emitPlaybackEventRef.current("channel_change");
      playbackRuntimeRef.current = null;
      currentItemRef.current = null;
      videoRef.current?.pause();
      setSelection(null);
      setDetail(null);
      setChannel(camera.channel);
      setFace({ channel: camera.channel, title: camera.name, subtitle: camera.location, genre: "WEBCAM", year: "----", era: null, network: camera.provider, station: camera.name, item: null });
      setIsTuning(false);
      setProfile((previous) => {
        const next = { ...previous, lastChannel: camera.channel };
        persistProfile(next);
        return next;
      });
      return;
    }

    try {
      const response = targetChannel === RANDO_CHANNEL
        ? await fetchNextSelection({
          sessionId, viewerId: profileRef.current.viewerId, currentItemId: currentItemRef.current,
          currentChannel: RANDO_CHANNEL, direction: "up", recentItemIds: [],
          capabilities: capabilityWithViewport(detectCapabilities()), mode: "rando",
          replayItemId: replay?.itemId, replayOffsetSeconds: replay?.offset
        }, controller.signal)
        : selectionFromLive(await fetchLiveChannel(targetChannel, controller.signal));

      if (gameModeRef.current || sequence !== requestSequenceRef.current) return;
      const responseChannel = Number.isInteger(response.channel)
        ? wrapChannel(response.channel)
        : targetChannel;

      if (!response.playback?.url || !isSafePlaybackUrl(response.playback.url)) {
        throw new Error("The selected source did not pass playback URL validation.");
      }

      if (currentItemRef.current && currentItemRef.current !== response.item.id) {
        emitPlaybackEventRef.current("channel_change");
      }

      if (targetChannel === RANDO_CHANNEL) {
        if (replay?.index !== undefined) randoIndexRef.current = replay.index;
        else {
          randoHistoryRef.current = [...randoHistoryRef.current.slice(0, randoIndexRef.current + 1), { itemId: response.item.id, offset: response.playback.start_offset_seconds ?? 0 }].slice(-100);
          randoIndexRef.current = randoHistoryRef.current.length - 1;
        }
        setRandoIndex(randoIndexRef.current);
      }
      currentItemRef.current = response.item.id;
      recentItemsRef.current = [...recentItemsRef.current.filter((id) => id !== response.item.id), response.item.id].slice(-RECENT_ITEM_LIMIT);
      setChannel(responseChannel);
      selectionReceivedAtRef.current = Date.now();
      setSelection(response);
      setFace(faceFromSelection(responseChannel, response));
      setDetail(response.item);
      setNoSignal(false);
      // Keep the signal transition visible until the browser actually plays.
      setServiceError(null);
      setProfile((previous) => {
        const next = { ...previous, lastChannel: responseChannel };
        persistProfile(next);
        return next;
      });
    } catch (error) {
      if (gameModeRef.current || isAbortError(error) || sequence !== requestSequenceRef.current) return;
      setSelection(null);
      currentItemRef.current = null;
      setDetail(null);
      setFace({ ...EMPTY_FACE, channel: targetChannel, title: "NO SIGNAL" });
      setNoSignal(true);
      setIsTuning(false);
      setServiceError(error instanceof Error ? error.message : "The archive signal was unavailable.");
    }
  }, [flushWatchTime, sessionId]);

  const navigateRando = (direction: -1 | 1) => {
    const index = randoIndexRef.current + direction;
    const entry = randoHistoryRef.current[index];
    if (direction === -1 && !entry) return;
    void performLiveTune(RANDO_CHANNEL, false, entry ? { ...entry, index } : undefined);
  };

  const restartRando = () => {
    const video = videoRef.current;
    if (!video || !selection || isTuning || syncPortrait()) return;
    flushWatchTime();
    try {
      video.currentTime = 0;
      const runtime = playbackRuntimeRef.current;
      if (runtime) { runtime.lastMediaTime = 0; runtime.lastSampleAt = performance.now(); }
      const entry = randoHistoryRef.current[randoIndexRef.current];
      if (entry) entry.offset = 0;
      void video.play().catch(() => undefined);
    } catch { setServiceError("This source could not restart."); }
  };

  const toggleFavorite = () => {
    if (!selection || isTuning) return;
    const item = selection.item;
    const next: Favorite[] = favorites.some((entry) => entry.id === item.id)
      ? favorites.filter((entry) => entry.id !== item.id)
      : [...favorites, { id: item.id, title: item.title }].slice(-500);
    setFavorites(next);
    setFavoritesPersistent(saveFavorites(next));
  };

  useEffect(() => {
    const controller = new AbortController();
    void fetchStations(controller.signal).then((publishedStations) => {
      if (controller.signal.aborted) return;
      const availableStations = tuningLineup(publishedStations);
      setStations(availableStations);
      const saved = initialStorage.profile.lastChannel;
      const target = availableStations.some((station) => station.channel === saved)
        ? saved!
        : availableStations.find((station) => station.channel === DEFAULT_CHANNEL)?.channel ?? availableStations.find((station) => station.kind === "broadcast")?.channel ?? WEBCAMS[0]!.channel;
      setChannel(target);
      setFace((previous) => ({ ...previous, channel: target }));
      void performLiveTune(target);
    }).catch(() => {
      if (controller.signal.aborted || gameModeRef.current) return;
      const fallbackStations = tuningLineup([]);
      setStations(fallbackStations);
      const saved = initialStorage.profile.lastChannel;
      void performLiveTune(saved === GUIDE_CHANNEL || (saved && webcamForChannel(saved)) ? saved : WEBCAMS[0]!.channel);
    });
    return () => {
      controller.abort();
      requestControllerRef.current?.abort();
      if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
    };
    // App startup should request exactly one initial selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeInput = useCallback((toGame: boolean) => {
    if (toGame === gameModeRef.current) return;
    if (toGame) emitPlaybackEvent("stop");
    guideChannelRef.current = false;
    setGuideChannel(false);
    gameModeRef.current = toGame;
    setGameMode(toGame);
    setInfoOpen(false);
    setGuideOpen(false);
    requestSequenceRef.current += 1;
    requestControllerRef.current?.abort();
    if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
    playbackRuntimeRef.current = null;
    videoRef.current?.pause();
    setSelection(null);
    webcamRef.current = null;
    setWebcam(null);
    if (toGame) {
      setGameMounted(true);
      setIsTuning(false);
    } else {
      void performLiveTune(channel);
    }
  }, [channel, emitPlaybackEvent, performLiveTune]);

  const tuneTo = useCallback((nextChannel: number, direction: "up" | "down") => {
    // A rocker step reconnects the aerial and immediately starts tuning.
    flushWatchTime();
    videoRef.current?.pause();
    if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
    detailControllerRef.current?.abort();
    gameModeRef.current = false;
    setGameMode(false);
    guideChannelRef.current = false;
    setGuideChannel(false);
    const safeChannel = wrapChannel(nextChannel);
    previousChannelRef.current = channel;
    setChannel(safeChannel);
    setFace((previous) => ({ ...previous, channel: safeChannel }));
    setIsTuning(true);
    setNoSignal(false);
    setInfoOpen(false);
    setGuideOpen(false);
    requestSequenceRef.current += 1;
    requestControllerRef.current?.abort();
    if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);
    tuneTimerRef.current = setTimeout(() => {
      void performLiveTune(safeChannel, false, undefined, true);
    }, TUNE_DEBOUNCE_MS);
  }, [channel, flushWatchTime, performLiveTune]);

  const handleVolume = useCallback((nextVolume: number, muteOverride?: boolean) => {
    const safeVolume = Math.min(1, Math.max(0, nextVolume));
    const nextMuted = muteOverride ?? safeVolume <= 0;
    volumeRef.current = safeVolume;
    mutedRef.current = nextMuted;
    if (safeVolume > 0 && !gameModeRef.current) waterCoolerAudio.unlock();
    setVolume(safeVolume);
    setMuted(nextMuted);
    setAudioUnlocked(true);
    setAudioUnlockHint(false);
    const nextProfile = { ...profileRef.current, volume: safeVolume, muted: nextMuted, audioUnlocked: true };
    profileRef.current = nextProfile;
    setProfile(nextProfile);
    persistProfile(nextProfile);
    const video = videoRef.current;
    if (gameModeRef.current) {
      void gameRef.current?.unlockAudio().then(setGameAudioUnlocked);
    } else if (guideChannelRef.current) {
      guidePlayerRef.current?.setVolume(safeVolume, nextMuted);
    } else if (video && !webcamRef.current) {
      video.volume = safeVolume;
      video.muted = nextMuted;
      if (!syncPortrait() && playbackRuntimeRef.current?.sequence === requestSequenceRef.current) void video.play().catch(() => undefined);
    }
  }, [syncPortrait]);

  const handleMediaError = useCallback(() => {
    if (gameModeRef.current || guideChannelRef.current || webcamRef.current) return;
    const runtime = playbackRuntimeRef.current;
    // Ignore duplicate errors and events from an already detached source.
    if (!runtime || runtime.sequence !== requestSequenceRef.current) return;
    const error = videoRef.current?.error;
    emitPlaybackEventRef.current("playback_error");
    playbackRuntimeRef.current = null;
    if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
    failedStationsRef.current.add(runtime.channel);
    setSelection(null);
    setServiceError(error?.message || "The source stopped responding.");
    if (tuneTimerRef.current) clearTimeout(tuneTimerRef.current);

    if (runtime.channel === RANDO_CHANNEL) {
      randoFailuresRef.current += 1;
      const retry = randoFailuresRef.current < 3;
      setNoSignal(!retry);
      setIsTuning(retry);
      if (retry) tuneTimerRef.current = setTimeout(() => void performLiveTune(RANDO_CHANNEL, true), 260);
      return;
    }

    const available = stationsRef.current.filter((station) => station.kind === "broadcast");
    const index = available.findIndex((station) => station.channel === runtime.channel);
    const next = Array.from({ length: available.length }, (_, step) => available[(index + step + 1) % available.length]!)
      .find((station) => !failedStationsRef.current.has(station.channel));
    setNoSignal(!next);
    setIsTuning(Boolean(next));
    if (next) {
      // A live station returns the same scheduled asset. Recover elsewhere,
      // trying each station only once before offering an intentional retry.
      tuneTimerRef.current = setTimeout(() => {
        setChannel(next.channel);
        void performLiveTune(next.channel, true);
      }, 260);
    }
  }, [performLiveTune]);

  const handleMediaReady = useCallback(() => {
    const video = videoRef.current;
    if (!video || gameModeRef.current || guideChannelRef.current || syncPortrait() || playbackRuntimeRef.current?.sequence !== requestSequenceRef.current) return;
    const source = video.currentSrc || video.src;
    const sequence = requestSequenceRef.current;
    const stillWatching = () => !syncPortrait() && !gameModeRef.current && !guideChannelRef.current && !webcamRef.current && sequence === requestSequenceRef.current
      && playbackRuntimeRef.current?.sequence === sequence && (video.currentSrc || video.src) === source;
    const markStarted = () => {
      if (!stillWatching()) return;
      const runtime = playbackRuntimeRef.current;
      if (runtime?.sequence === requestSequenceRef.current) completeUsageTune(runtime.channel);
      if (runtime && !runtime.startEventSent) {
        runtime.startEventSent = true;
        emitPlaybackEventRef.current("start");
      }
      if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
      failedStationsRef.current.clear();
      randoFailuresRef.current = 0;
      setNoSignal(false);
      setIsTuning(false);
    };
    video.volume = volumeRef.current;
    // A first session should try the viewer's saved preference (audible by
    // default). If policy rejects it, handleMediaReady immediately retries
    // muted without interrupting the picture.
    video.muted = mutedRef.current;
    void video.play().then(() => {
      markStarted();
    }).catch((error: unknown) => {
      if (!stillWatching() || isAbortError(error)) return;
      if (!(error instanceof DOMException) || error.name !== "NotAllowedError" || video.muted) {
        handleMediaError();
        return;
      }

      // Browser policy rejected audible autoplay. Continue with visual playback.
      video.muted = true;
      mutedRef.current = true;
      setMuted(true);
      setAudioUnlocked(false);
      setAudioUnlockHint(true);
      const nextProfile = { ...profileRef.current, muted: true, audioUnlocked: false };
      profileRef.current = nextProfile;
      setProfile(nextProfile);
      persistProfile(nextProfile);
      void video.play().then(() => {
        markStarted();
      }).catch((error: unknown) => {
        if (!stillWatching() || isAbortError(error)) return;
        handleMediaError();
      });
    });
  }, [handleMediaError, syncPortrait]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (gameMode || !selection?.playback.url) {
      video.pause();
      video.removeAttribute("src");
      video.load();
      return;
    }
    const playback = selection.playback;
    const baseOffset = Math.max(0, toSafeNumber(playback.start_offset_seconds, 0));
    const scheduledOffset = () => Math.max(0, baseOffset + (selection.broadcast ? Math.floor((Date.now() - selectionReceivedAtRef.current) / 1_000) : 0));

    video.pause();
    video.removeAttribute("src");
    video.load();
    video.src = playback.url;
    video.preload = "metadata";
    video.volume = volumeRef.current;
    video.muted = mutedRef.current;

    // Establish telemetry before loading metadata: unavailable/unsupported
    // sources can fail without ever firing loadedmetadata.
    playbackRuntimeRef.current = {
      sequence: requestSequenceRef.current,
      itemId: selection.item.id,
      channel: selection.channel,
      startedAt: Date.now(),
      startOffset: baseOffset,
      watchSeconds: 0,
      lastMediaTime: null,
      lastSampleAt: performance.now(),
      startEventSent: false
    };
    if (!portraitRef.current) playbackStartTimerRef.current = setTimeout(handleMediaError, PLAYBACK_START_TIMEOUT_MS);

    const setOffset = () => {
      if (gameModeRef.current) return;
      const offset = scheduledOffset();
      if (offset > 0 && Number.isFinite(video.duration) && offset < video.duration) {
        try {
          video.currentTime = offset;
        } catch {
          // Some providers reject a seek before enough metadata is buffered.
        }
      }
      const runtime = playbackRuntimeRef.current;
      if (runtime) {
        runtime.startOffset = offset;
        runtime.lastMediaTime = video.currentTime;
        runtime.lastSampleAt = performance.now();
      }
    };

    video.addEventListener("loadedmetadata", setOffset, { once: true });
    video.addEventListener("loadeddata", handleMediaReady, { once: true });
    video.addEventListener("canplay", handleMediaReady, { once: true });
    return () => {
      if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
      playbackRuntimeRef.current = null;
      video.removeEventListener("loadedmetadata", setOffset);
      video.removeEventListener("loadeddata", handleMediaReady);
      video.removeEventListener("canplay", handleMediaReady);
      video.pause();
    };
  }, [gameMode, handleMediaError, handleMediaReady, selection]);

  useEffect(() => {
    // An older passive effect must not consume a newer portrait suspension.
    if (!portrait && syncPortrait()) return;
    const resuming = portraitSuspendedRef.current && !portrait;
    const video = videoRef.current;
    if (!video || gameMode || !selection) return;
    if (!portrait && !resuming) return;
    if (playbackStartTimerRef.current) clearTimeout(playbackStartTimerRef.current);
    if (portrait) {
      video.pause();
      if (playbackRuntimeRef.current) playbackRuntimeRef.current.lastMediaTime = null;
      return;
    }
    portraitSuspendedRef.current = false;
    if (selection.broadcast) {
      const serverNow = Date.parse(selection.broadcast.server_time) + (Date.now() - selectionReceivedAtRef.current);
      if (serverNow >= Date.parse(selection.broadcast.end_at)) {
        void performLiveTune(selection.channel);
        return;
      }
    }
    // Resume the same source after rotation, retaining the saved sound state.
    // Live stations catch up to the shared clock without counting the seek as viewing.
    const runtime = playbackRuntimeRef.current;
    if (runtime) runtime.lastMediaTime = null;
    if (selection.broadcast && Number.isFinite(video.duration)) {
      const expected = selection.broadcast.playback_offset_seconds + (Date.now() - selectionReceivedAtRef.current) / 1_000;
      if (expected >= video.duration) {
        handleMediaError();
        return;
      }
      try { video.currentTime = Math.max(0, expected); } catch { /* metadata may still be loading */ }
    }
    playbackStartTimerRef.current = setTimeout(handleMediaError, PLAYBACK_START_TIMEOUT_MS);
    // play() waits for data after a catch-up seek; one-shot load listeners may
    // already have fired before rotation.
    handleMediaReady();
  }, [gameMode, handleMediaError, handleMediaReady, performLiveTune, portrait, selection, syncPortrait, viewport]);

  useEffect(() => {
    if (!selection?.broadcast || !videoRef.current) return;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration) || video.paused) return;
      const expected = selection.broadcast!.playback_offset_seconds + Math.floor((Date.now() - selectionReceivedAtRef.current) / 1_000);
      if (expected >= 0 && expected < video.duration && Math.abs(video.currentTime - expected) > 3) {
        try { video.currentTime = expected; } catch { /* best-effort shared-clock correction */ }
      }
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [selection]);

  const handleTimeUpdate = useCallback(() => {
    if (!syncPortrait()) flushWatchTime();
  }, [flushWatchTime, syncPortrait]);

  const handleMediaEnded = useCallback(() => {
    if (gameModeRef.current || guideChannelRef.current || webcamRef.current) return;
    const broadcast = selection?.broadcast;
    if (broadcast) {
      const serverNow = Date.parse(broadcast.server_time) + (Date.now() - selectionReceivedAtRef.current);
      // A file that ends well before its scheduled slot would be selected
      // again by /now. Treat it as a failed source instead of replaying it.
      if (Date.parse(broadcast.end_at) - serverNow > 2_000) {
        handleMediaError();
        return;
      }
    }
    emitPlaybackEvent("natural_end");
    playbackRuntimeRef.current = null;
    setIsTuning(true);
    setNoSignal(false);
    void performLiveTune(channel);
  }, [channel, emitPlaybackEvent, handleMediaError, performLiveTune, selection]);

  useEffect(() => {
    const handleVisibility = () => {
      // visibilitychange fires after the document becomes hidden. Include the
      // final foreground sample, then reset it so hidden playback adds none.
      flushWatchTime(document.visibilityState === "hidden");
      const runtime = playbackRuntimeRef.current;
      if (!runtime) return;
      if (document.visibilityState === "hidden") {
        emitPlaybackEvent("page_hidden");
        runtime.lastMediaTime = null;
      } else if (videoRef.current) {
        runtime.lastMediaTime = videoRef.current.currentTime;
        runtime.lastSampleAt = performance.now();
      }
    };
    const handleUnload = () => emitPlaybackEvent("session_end", true);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handleUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [emitPlaybackEvent, flushWatchTime]);

  const openInfo = useCallback((event?: ReactMouseEvent<HTMLElement>) => {
    const trigger = event?.currentTarget ?? document.activeElement;
    infoTriggerRef.current = trigger instanceof HTMLElement ? trigger : null;
    setInfoOpen(true);
    setGuideOpen(false);
  }, []);

  useEffect(() => {
    const itemId = selection?.item.id;
    setDetailLoading(false);
    if (!infoOpen || gameMode || webcam || guideChannel || !itemId || isTuning) return;
    const controller = new AbortController();
    detailControllerRef.current = controller;
    setDetailLoading(true);
    void fetchItemDetails(itemId, controller.signal).then((item) => {
      if (!controller.signal.aborted && currentItemRef.current === itemId && item.id === itemId) setDetail(item);
    }).catch(() => undefined).finally(() => {
      if (!controller.signal.aborted && currentItemRef.current === itemId) setDetailLoading(false);
    });
    return () => controller.abort();
  }, [gameMode, guideChannel, infoOpen, isTuning, selection?.item.id, webcam]);

  const loadGuide = useCallback(() => {
    guideControllerRef.current?.abort();
    const controller = new AbortController();
    guideControllerRef.current = controller;
    setGuideLoading(true);
    setGuideError(false);
    setGuide(null);
    const from = new Date();
    const to = new Date(from.getTime() + 36 * 3_600_000);
    void fetchGuide(from, to, controller.signal).then((response) => {
      if (!controller.signal.aborted) setGuide(response);
    }).catch(() => {
      if (!controller.signal.aborted) setGuideError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setGuideLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!guideOpen) return;
    loadGuide();
    return () => guideControllerRef.current?.abort();
  }, [guideOpen, loadGuide]);

  const openGuide = useCallback((event?: ReactMouseEvent<HTMLElement>) => {
    if (gameModeRef.current) changeInput(false);
    const trigger = event?.currentTarget ?? document.activeElement;
    infoTriggerRef.current = trigger instanceof HTMLElement ? trigger : null;
    setInfoOpen(false);
    if (!guideOpen) usage.event("guide_open");
    setGuideOpen(true);
  }, [changeInput, guideOpen, usage]);

  const closeInfo = useCallback(() => {
    setInfoOpen(false);
    setGuideOpen(false);
    window.setTimeout(() => (infoTriggerRef.current ?? (gameModeRef.current ? gameTitleRef.current : titleRef.current))?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!infoOpen && !guideOpen) return;
    const timer = window.setTimeout(() => panelCloseRef.current?.focus(), 0);
    const containFocus = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const panel = panelCloseRef.current?.closest<HTMLElement>("[role=dialog]");
      const candidates = panel ? Array.from(panel.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'))
        .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0) : [];
      const controls = candidates.filter((element) => {
        if (!(element instanceof HTMLInputElement) || element.type !== "radio" || !element.name) return true;
        const group = candidates.filter((candidate): candidate is HTMLInputElement => candidate instanceof HTMLInputElement && candidate.type === "radio" && candidate.name === element.name);
        return element === (group.find((radio) => radio.checked) ?? group[0]);
      });
      if (!controls.length) return;
      // WebKit's platform Tab preferences may skip buttons entirely. Advance
      // explicitly within the dialog, retaining one tab stop per radio group.
      const index = controls.indexOf(document.activeElement as HTMLElement);
      const next = index < 0 ? (event.shiftKey ? controls.length - 1 : 0) : (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
      event.preventDefault();
      controls[next]?.focus();
    };
    document.addEventListener("keydown", containFocus);
    return () => { window.clearTimeout(timer); document.removeEventListener("keydown", containFocus); };
  }, [guideOpen, infoOpen]);

  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && (infoOpen || guideOpen)) closeInfo();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeInfo, guideOpen, infoOpen]);

  const feedback = useCallback((signal: "MORE_LIKE_THIS" | "LESS_LIKE_THIS") => {
    const itemId = currentItemRef.current;
    if (!itemId || feedbackControllerRef.current) return;
    const controller = new AbortController();
    feedbackControllerRef.current = controller;
    setFeedbackState({ itemId, signal, status: "pending" });
    void postFeedback({ session_id: sessionId, item_id: itemId, signal }, controller.signal).then(() => {
      if (!controller.signal.aborted && currentItemRef.current === itemId) setFeedbackState({ itemId, signal, status: "saved" });
    }).catch(() => {
      if (!controller.signal.aborted && currentItemRef.current === itemId) setFeedbackState({ itemId, signal, status: "error" });
    }).finally(() => {
      if (feedbackControllerRef.current === controller) feedbackControllerRef.current = null;
    });
  }, [sessionId]);

  useEffect(() => () => feedbackControllerRef.current?.abort(), []);

  const guideAudioBlocked = useCallback(() => {
    if (!guideChannelRef.current) return;
    setMuted(true);
    setAudioUnlocked(false);
    setAudioUnlockHint(true);
  }, []);

  const screenStatus = guideChannel ? "TV GUIDE · ALL TIMES EASTERN" : gameMode ? `GAME MODE · ${gameTitle}` : webcam ? `WEBCAM · ${webcam.location}` : noSignal ? "NO SIGNAL. RETRY AVAILABLE." : isTuning ? "TUNING" : muted ? "PICTURE ON · SOUND MUTED" : "ON AIR";
  const videoSource = selection?.playback.url;
  const shellClass = `tv-object ${gameMode ? "tv-object-game" : ""} ${isTuning ? "tv-object-tuning" : ""} ${infoOpen ? "tv-object-info-open" : ""}`;
  const captionUrl = safeCaptionTrackUrl(selection?.playback.caption_url, window.location.href);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !captionUrl) return;
    const showCaptions = () => {
      const track = video.querySelector("track");
      if (track) track.track.mode = "showing";
    };
    // WebKit can leave a default track disabled after load()/source changes.
    // Explicit selection starts the same-origin fetch and displays source cues.
    showCaptions();
    video.addEventListener("loadedmetadata", showCaptions);
    video.textTracks.addEventListener("addtrack", showCaptions);
    return () => {
      video.removeEventListener("loadedmetadata", showCaptions);
      video.textTracks.removeEventListener("addtrack", showCaptions);
    };
  }, [captionUrl, videoSource]);

  const title = gameMode ? gameTitle : face.title.trim() || "UNTITLED PROGRAM";
  const subtitle = gameMode ? (ARCADE_GAMES.some(game => game.title === gameTitle) ? "ARCADE CARTRIDGE" : "PLAYER 1 · NES") : face.subtitle?.trim();


  return (
    <div className="app-surface" data-skin={skin}>
      <div className="screen-reader-status" role="status" aria-live="polite">{screenStatus}</div>
      <div className="portrait-only"><PortraitFallback /></div>
      <main className="landscape-only" aria-label="SerendipTV television">
        <section className={shellClass}>
          <div className="cabinet-rim" aria-hidden="true" />
          <div className="cabinet-surface" inert={infoOpen || guideOpen}>
            <div className="crt-column">
              <div className="crt-bezel">
                <div className={`crt-aperture ${guideChannel ? "crt-aperture-guide" : ""} ${webcam && !gameMode ? "crt-aperture-webcam" : ""}`}>
                  <video
                    ref={videoRef}
                    className="crt-video"
                    hidden={gameMode || !!webcam || guideChannel}
                    playsInline
                    preload="metadata"
                    aria-label={selection?.item.title ? `Now playing: ${selection.item.title}` : "SerendipTV video"}
                    onPlay={syncPortrait}
                    onPlaying={syncPortrait}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleMediaEnded}
                    onError={handleMediaError}
                  >
                    {captionUrl ? <track key={captionUrl} kind="captions" src={captionUrl} srcLang={selection?.playback.caption_language || "en"} label={selection?.playback.caption_label || "Captions"} default /> : null}
                  </video>
                  {guideMounted ? <Suspense fallback={guideChannel && !gameMode ? <span className="crt-tuning-copy">RECEIVING PROGRAM LISTINGS…</span> : null}><GuideChannel active={guideChannel && !gameMode} ads={ads} adsUnobstructed={!infoOpen && !guideOpen} onListingsReady={setGuideUsable} ref={guidePlayerRef} volume={volume} muted={muted} suspended={portrait} onAudioBlocked={guideAudioBlocked} /></Suspense> : null}
                  {!gameMode && webcam ? <Suspense fallback={<span className="crt-tuning-copy">CONNECTING WEBCAM…</span>}><WebcamPlayer key={webcam.channel} camera={webcam} volume={volume} muted={muted} onPlaying={onWebcamPlaying} /></Suspense> : null}
                  {!gameMode && !webcam && !guideChannel ? <StaticOverlay active={isTuning || !videoSource} noSignal={noSignal} /> : null}
                  {gameMounted ? (
                    <GameLoadBoundary active={gameMode} onError={() => setCartridgeState("error")}>
                    <Suspense fallback={gameMode ? <span className="crt-tuning-copy">CONNECTING CARTRIDGE…</span> : null}>
                      <GameConsole ref={gameRef} controllerMount={controllerMount} active={gameMode} suspended={infoOpen || portrait} volume={volume} muted={muted} onTitleChange={cartridgeInserted} onCartridgeStateChange={setCartridgeState} />
                    </Suspense>
                    </GameLoadBoundary>
                  ) : null}
                  <div className="crt-glass" aria-hidden="true" />
                  {!gameMode && !webcam && !guideChannel && !selection && !noSignal ? <span className="crt-tuning-copy">TUNING THE AIRWAVES</span> : null}
                  {!gameMode && !webcam && !guideChannel && noSignal ? <button className="static-retry" type="button" onClick={() => void performLiveTune(channel)}>RETRY SIGNAL</button> : null}
                </div>
              </div>
              <div className={`crt-bottom-bar ${gameMode ? "" : "crt-bottom-bar-tv"} ${!gameMode && channel === RANDO_CHANNEL ? "crt-bottom-bar-rando" : ""}`}>
                <button className="input-switch" type="button" role="switch" aria-label="Game mode" aria-checked={gameMode} onClick={() => changeInput(!gameModeRef.current)}>
                  <span className={!gameMode ? "input-selected" : ""}>TV</span>
                  <span className="input-switch-track" aria-hidden="true"><span /></span>
                  <span className={gameMode ? "input-selected" : ""}>GAME</span>
                </button>
              {!gameMode && channel === RANDO_CHANNEL ? <div className="rando-controls" role="group" aria-label="Rando playback controls">
                <button type="button" className="panel-action" disabled={isTuning || randoIndex <= 0} onClick={() => navigateRando(-1)}>Previous</button>
                <button type="button" className="panel-action" disabled={isTuning} onClick={() => navigateRando(1)}>Next</button>
                <button type="button" className="panel-action" disabled={isTuning || !selection} onClick={restartRando}>Start from Beginning</button>
                <button type="button" className="panel-action" disabled={isTuning || !selection} aria-pressed={favorites.some((entry) => entry.id === selection?.item.id)} onClick={toggleFavorite}>{favorites.some((entry) => entry.id === selection?.item.id) ? "Saved ★" : "Favorite ☆"}</button>
              </div> : null}
              {gameMode ? <button ref={gameTitleRef} type="button" className="title-plate" onClick={openInfo} aria-label={`${gameMode ? "Game" : "Program"} information for ${title}${subtitle ? `: ${subtitle}` : ""}`}>
                <span className="title-plate-screw" aria-hidden="true" />
                <span className="title-plate-copy"><strong>{title}</strong>{subtitle ? <small>{subtitle}</small> : null}</span>
                <span className="title-plate-screw" aria-hidden="true" />
              </button> : null}
              </div>
            </div>

            <TelevisionPanel
              hidden={gameMode}
              ads={ads}
              adEligible={Boolean((selection || (guideChannel && guideUsable)) && !gameMode && !portrait && !webcam && !isTuning && !noSignal && !infoOpen && !guideOpen)}
              channel={channel}
              stations={stations}
              title={isTuning ? "TUNING IN…" : noSignal ? "NO SIGNAL" : title}
              subtitle={isTuning || noSignal ? null : subtitle ?? null}
              item={isTuning || noSignal ? null : selection?.item ?? null}
              watchingNow={watchingNow.total}
              stationWatchingNow={watchingNow.station}
              volume={volume}
              muted={muted}
              chatEnabled={!portrait && !gameMode}
              titleRef={titleRef}
              onTune={tuneTo}
              onInfo={openInfo}
              onGuide={openGuide}
              volumeControl={<VolumeControl volume={volume} muted={muted} audioUnlockHint={audioUnlockHint} onVolume={handleVolume} onMute={() => handleVolume(volumeRef.current || 0.42, !mutedRef.current)} />}
            />
            {gameMode ? <div className="game-cartridge-bay"><CartridgeSlot title={gameTitle} state={cartridgeState} insertion={cartridgeInsertion} onChange={openInfo} /></div> : null}
            <div ref={setControllerMount} className="cabinet-game-controls" />
          </div>

          {infoOpen ? (
            <div className="service-panel-wrap">
              {gameMode ? <GameInfoPanel title={gameTitle} closeRef={panelCloseRef} onClose={closeInfo} gameRef={gameRef} volume={volume} muted={muted || !gameAudioUnlocked} onVolume={handleVolume} skin={skin} onSkinChange={handleSkinChange} /> : guideChannel ? <GuideChannelInfo onClose={closeInfo} closeRef={panelCloseRef} /> : webcam ? <WebcamInfo camera={webcam} onClose={closeInfo} closeRef={panelCloseRef} /> : <InfoPanel face={face} detail={detail} loading={detailLoading} onClose={closeInfo} onFeedback={feedback} feedbackState={feedbackState?.itemId === face.item?.id ? feedbackState : null} closeRef={panelCloseRef} skin={skin} onSkinChange={handleSkinChange} favorites={favorites} favoritesPersistent={favoritesPersistent} onFavoritePlay={(favorite) => { closeInfo(); setChannel(RANDO_CHANNEL); void performLiveTune(RANDO_CHANNEL, false, { itemId: favorite.id, offset: 0 }, true); }} />}
            </div>
          ) : null}
          {guideOpen ? (
            <div className="service-panel-wrap">
              <GuidePanel guide={guide} loading={guideLoading} error={guideError} onRetry={loadGuide} currentChannel={channel} onClose={closeInfo} onTune={(stationChannel) => tuneTo(stationChannel, directionBetween(channel, stationChannel))} closeRef={panelCloseRef} />
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
}

export default function App() {
  const [access, setAccess] = useState<"checking" | "unlocked" | "locked" | "error">("checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setAccess("checking");
    void fetchAccessStatus(controller.signal).then((status) => {
      if (!controller.signal.aborted) setAccess(!status.gate_enabled || status.unlocked ? "unlocked" : "locked");
    }).catch(() => {
      if (!controller.signal.aborted) setAccess("error");
    });
    return () => controller.abort();
  }, [attempt]);

  if (access === "unlocked") return <ViewerApp />;
  if (access === "locked") return <AccessGate checking={false} onUnlocked={() => setAccess("unlocked")} />;
  return <main className="receiver-access" aria-label="SerendipTV connection">
    <section className="receiver-cabinet">
      <div className="receiver-panel" aria-busy={access === "checking"}>
        <div className="receiver-screws" aria-hidden="true"><i /><i /><i /><i /></div>
        <div className="receiver-brand">SERENDIPTV</div>
        <div className="receiver-rule" aria-hidden="true" />
        <h1>{access === "error" ? "Connection unavailable" : "Connecting your television"}</h1>
        <p className="receiver-instruction" role="status">{access === "error" ? "Couldn’t connect to SerendipTV. Please try again." : "Checking the signal…"}</p>
        {access === "error" ? <button type="button" className="receiver-unlock" onClick={() => setAttempt((value) => value + 1)}>Retry connection</button> : null}
      </div>
    </section>
  </main>;
}
